// The STAT grammar: label / large numeral / qualifier (#11426; proportional figures, #13380).

import React, { useRef, useState } from "react";
import { Tooltip } from "@mui/material";

/** Unknown renders this — never a silent zero, never "$NaN". */
export const EM_DASH = "—";

/**
 * Past this many characters a value is prose, not a figure, and renders at
 * reading size.
 *
 * The STAT grammar is label / large numeral / qualifier, and it assumes the
 * value is a figure. Real records put text in leading fields anyway — a
 * taxonomy path, a strategy name — and at 27px those wrap to three lines and
 * leave the strip ragged. Stepping the type down keeps the row even and reads
 * better than either truncating the value (dishonest) or refusing to show it.
 */
const FIGURE_MAX_CHARS = 14;

export interface StatProps {
  label: string;
  /** Absent renders an em dash. Zero is a measurement and renders as zero. */
  value?: string | number | null;
  unit?: string | null;
  qualifier?: React.ReactNode;
  /** Draws the qualifier as a thing to look at rather than a footnote. */
  qualifierState?: "watch" | null;
  /**
   * Editing controls drawn over the card's top-right corner (the Appearance editor's Remove ×,
   * #14420). Positioned by the node passed; the card only anchors it.
   */
  tools?: React.ReactNode;
  /** A class on the card itself, for an editor that reveals `tools` on hover. */
  className?: string;
}

export const Stat: React.FC<StatProps> = ({
  label,
  value,
  unit,
  qualifier,
  qualifierState,
  tools,
  className,
}) => {
  const known = value !== null && value !== undefined && value !== "";
  const text = known ? String(value) : EM_DASH;
  const isFigure = !known || text.length <= FIGURE_MAX_CHARS;
  // A value with no space in it (a figure, a URL, an email address) cannot be read wrapped, so it
  // keeps one line and ends in an ellipsis at the card's edge; prose wraps and clamps at three
  // lines (round 42: a website ran past its card).
  const oneLine = isFigure || !/\s/.test(text);
  const valueRef = useRef<HTMLSpanElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  return (
    <div
      className={className}
      style={{
        // Anchors `tools` at the corner; a card without them is positioned as before.
        position: tools ? "relative" : undefined,
        // Subgrid, so the label row is exactly as tall as the tallest label in
        // THIS row of cards and no taller. Reserving two lines unconditionally
        // aligned the values but left dead space whenever nothing wrapped;
        // letting each card size itself put the values on three baselines.
        display: "grid",
        gridTemplateRows: "subgrid",
        gridRow: "span 3",
        gap: "var(--fp-gap-hair)",
        padding: "var(--fp-card-pad)",
        backgroundColor: "var(--fp-surface)",
        border: "1px solid var(--fp-border)",
        borderRadius: "var(--fp-card-r)",
        boxShadow: "var(--fp-lift)",
        minWidth: 0,
        // Fills its track. The cap that keeps a sparse strip's cards
        // adjacent lives on the STRIP (see StatStrip), which narrows the
        // tracks themselves; the per-card max below is therefore a no-op
        // in every multi-column layout and only binds in the one place
        // the strip cap cannot reach — the single-column band, where the
        // track is the whole (narrow) container and the card would
        // otherwise stretch past its designed measure (#11426).
        width: "100%",
        maxWidth: "var(--fp-stat-max)",
      }}
    >
      {/* Label, value and qualifier are the card's three subgrid rows, so a
          strip mixing one-line and two-line labels still puts every value on
          one baseline. */}
      <div
        style={{
          fontFamily: "var(--fp-font-ui)",
          // Sentence case as written, never shouted in caps (#13380).
          fontSize: "var(--fp-t-meta)",
          fontWeight: "var(--fp-w-strong)" as unknown as number,
          lineHeight: 1.4,
          color: "var(--fp-ink-3)",
        }}
      >
        {label}
      </div>

      <div
        style={{
          fontFamily: "var(--fp-font-num)",
          // Proportional digits: a standalone figure, not a column (#13380).
          fontFeatureSettings: "var(--fp-feat-figure)",
          fontVariantNumeric: "proportional-nums",
          fontSize: isFigure ? "var(--fp-t-stat)" : "var(--fp-t-lede)",
          fontWeight: isFigure
            ? ("var(--fp-w-stat)" as unknown as number)
            : ("var(--fp-w-strong)" as unknown as number),
          letterSpacing: isFigure
            ? "var(--fp-track-stat)"
            : "var(--fp-track-head)",
          lineHeight: isFigure ? undefined : "var(--fp-lh-body)",
          color: known ? "var(--fp-ink)" : "var(--fp-ink-4)",
          display: "flex",
          alignItems: "baseline",
          gap: "var(--fp-gap-hair)",
          minWidth: 0,
        }}
      >
        {/* The whole value on hover, only when the card cuts it short: repeating visible text
            on hover is noise (#11426's hover ruling). */}
        <Tooltip
          title={text}
          placement="top-start"
          disableInteractive
          open={tooltipOpen}
          onOpen={() => {
            const node = valueRef.current;
            setTooltipOpen(
              node !== null &&
                (node.scrollWidth > node.clientWidth ||
                  node.scrollHeight > node.clientHeight)
            );
          }}
          onClose={() => setTooltipOpen(false)}
        >
          <span
            ref={valueRef}
            data-stat-value=""
            style={
              oneLine
                ? {
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }
                : {
                    // Prose stays a stat, not a paragraph: past three lines the
                    // value clamps, and the Facts tab is where it is read in
                    // full. Unclamped, one long value stretched the subgrid's
                    // value row and with it every card in the strip (#11426).
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    overflowWrap: "anywhere",
                    minWidth: 0,
                  }
            }
          >
            {text}
          </span>
        </Tooltip>
        {known && unit ? (
          <span
            style={{
              fontSize: "var(--fp-t-lede)",
              fontWeight: "var(--fp-w-primary)" as unknown as number,
              color: "var(--fp-ink-3)",
            }}
          >
            {unit}
          </span>
        ) : null}
      </div>

      {/* Always present, even with nothing in it: every card has to span the
          same three rows or the subgrid falls out of step. An empty row
          collapses to nothing, so a strip where no stat carries a qualifier
          costs no height. */}
      <div
        style={{
          fontFamily: "var(--fp-font-ui)",
          fontSize: "var(--fp-t-meta)",
          lineHeight: "var(--fp-lh-body)",
          color:
            qualifierState === "watch"
              ? "var(--fp-st-watch)"
              : "var(--fp-ink-3)",
        }}
      >
        {qualifier}
      </div>
      {tools}
    </div>
  );
};

export interface StatStripProps {
  children?: React.ReactNode;
}

/**
 * The 3–5 cards answering the question the page is opened to ask.
 *
 * Renders nothing when it holds nothing: an empty component is absent, not an
 * empty state, which is what lets page length vary honestly with the record.
 *
 * `auto-fit` with a `1fr` max, so a full strip divides the content width
 * exactly and its right edge lands on the rail's — the page reads as one grid
 * rather than three independently placed ones.
 *
 * The cap that stops two stats becoming two half-page cards lives on the
 * STRIP: its own max-width is exactly N cards at `--fp-stat-max` plus the
 * gaps between them, so when the row has slack the tracks themselves narrow
 * to the card cap and the cards sit adjacent at the standard gap. Capping
 * the track sizing function instead is the obvious move and it is wrong —
 * the browser counts `auto-fit` repetitions from the max track sizing
 * function, so `minmax(196px, 280px)` fits three cards on a 1113px row and
 * wraps the fourth. And a per-card max-width inside a full-width `1fr`
 * track is the shipped mistake this replaces: the second card sat stranded
 * at mid-page behind its track's slack (#11426).
 */
export const StatStrip: React.FC<StatStripProps> = ({ children }) => {
  // toArray, not count: count includes false/null children, which would
  // silently oversize the strip's cap by a card and defeat the empty check
  // the moment a call site writes `{cond && <Stat/>}`.
  const count = React.Children.toArray(children).length;
  if (count === 0) return null;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns:
          "repeat(auto-fit, minmax(min(100%, var(--fp-stat-min)), 1fr))",
        maxWidth: `calc(${count} * var(--fp-stat-max) + ${count - 1} * var(--fp-gap))`,
        gap: "var(--fp-gap)",
      }}
    >
      {children}
    </div>
  );
};
